/**
 * `/projects/:id/audits/new` — create an audit (`CreateAudit.dc.html`, §7.2).
 *
 * The screen exists for one reason: **nobody should discover what was inherited after
 * the audit exists.** The preview is a dry run of exactly the plan the server will
 * materialise, so the three numbers that matter — inherited, to verify, must re-check —
 * are on screen before anything is written.
 *
 * `mustRecheck` is the honest half. A proof-of-execution item cannot be carried forward
 * however recently it was verified, and the screen says which items and why rather than
 * quietly resetting them to Pending.
 */

import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { MarkerChip, StateChip } from '@portal/ui';
import type { InheritancePreview } from '@portal/contracts';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const CARD: CSSProperties = {
  background: 'var(--sf)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-lg)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const NOTE: CSSProperties = { fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 };
const MONO: CSSProperties = { fontFamily: 'var(--font-mono)' };

const FIELD: CSSProperties = {
  height: 30,
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  fontSize: 12,
  padding: '0 8px',
};

/** Why an item cannot be carried forward, in words an auditor can act on. */
const RECHECK_REASON: Record<
  InheritancePreview['mustRecheck'][number]['reason'],
  { label: string; detail: string }
> = {
  PROOF_OF_EXECUTION: {
    label: 'proof of execution',
    detail: 'A drill or test that happened last cycle does not prove one happened this cycle.',
  },
  EVIDENCE_EXPIRED: {
    label: 'evidence expired',
    detail: 'The answer was true when it was given, and its freshness window has closed.',
  },
  PREVIOUS_STATE_NOT_CARRIED: {
    label: 'not carried',
    detail: 'The previous answer was not a verification, so there is nothing to inherit.',
  },
};

function Metric({
  value,
  caption,
  tone,
}: {
  readonly value: number;
  readonly caption: string;
  readonly tone?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontWeight: 600,
          fontSize: 20,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          ...(tone === undefined ? {} : { color: tone }),
        }}
      >
        {value}
      </span>
      <span style={{ ...LABEL, fontSize: 9.5 }}>{caption}</span>
    </div>
  );
}

export function AuditNewRoute() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [auditTypeId, setAuditTypeId] = useState('');
  const [periodLabel, setPeriodLabel] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [auditorIds, setAuditorIds] = useState<string[]>([]);

  const project = useQuery({ queryKey: ['project', id], queryFn: () => api.project(id) });
  const auditTypes = useQuery({ queryKey: ['audit-types'], queryFn: () => api.auditTypes() });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });

  const selectedType = auditTypes.data?.find((t) => t.id === auditTypeId) ?? null;

  // Sensible default period from the type's cadence, so the field is rarely retyped.
  const suggestedPeriod = useMemo(() => {
    const now = new Date();
    const q = Math.floor(now.getUTCMonth() / 3) + 1;
    if (selectedType === null) return '';
    if (selectedType.key.includes('month')) {
      return now.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
    if (selectedType.key.includes('quarter')) return `Q${String(q)} ${String(now.getUTCFullYear())}`;
    if (selectedType.key.includes('annual')) return String(now.getUTCFullYear());
    return '';
  }, [selectedType]);

  const effectivePeriod = periodLabel.trim() === '' ? suggestedPeriod : periodLabel.trim();

  const preview = useQuery({
    queryKey: ['audit-preview', id, auditTypeId, effectivePeriod],
    enabled: auditTypeId !== '' && effectivePeriod !== '',
    queryFn: () =>
      api.previewAudit({
        projectId: id,
        auditTypeId,
        periodLabel: effectivePeriod,
        dueDate: dueDate === '' ? null : new Date(`${dueDate}T00:00:00.000Z`).toISOString(),
        inheritFromAuditId: null,
      }),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createAudit({
        projectId: id,
        auditTypeId,
        periodLabel: effectivePeriod,
        dueDate: dueDate === '' ? null : new Date(`${dueDate}T00:00:00.000Z`).toISOString(),
        inheritFromAuditId: null,
        assignedAuditorIds: auditorIds,
      }),
    onSuccess: (created) => {
      void navigate(`/audits/${created.id}`);
    },
  });

  if (project.isError) {
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong style={{ color: 'var(--ba)' }}>
          {project.error instanceof ApiClientError
            ? project.error.message
            : 'That project could not be loaded.'}
        </strong>
        <Link to="/" style={{ fontSize: 12.5 }}>
          Back to the portfolio
        </Link>
      </div>
    );
  }

  const counts = preview.data?.counts ?? null;

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--mu)' }}
        >
          <Link to="/">Portfolio</Link>
          <span>›</span>
          <Link to={`/projects/${id}`}>{project.data?.project.name ?? 'Project'}</Link>
          <span>›</span>
          <span style={{ color: 'var(--tx)', fontWeight: 600 }}>New audit</span>
        </div>
        <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}>
          New audit
        </h1>
        <span style={NOTE}>
          Nothing is written until you create it. The preview below is the plan the server will
          materialise, item for item.
        </span>
      </header>

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        {/* ── the form ── */}
        <div style={{ ...CARD, width: 380, flex: '0 0 380px', padding: '14px 15px', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={LABEL}>Audit type</span>
            <select
              value={auditTypeId}
              onChange={(event) => {
                setAuditTypeId(event.target.value);
                setPeriodLabel('');
              }}
              style={FIELD}
            >
              <option value="">Choose a type…</option>
              {(auditTypes.data ?? []).map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            {selectedType !== null && (
              <span style={{ ...NOTE, fontSize: 10.5 }}>
                {selectedType.description}
                {selectedType.scopeRefs.length > 0 && (
                  <>
                    {' '}
                    Covers <b style={{ color: 'var(--tx)' }}>{selectedType.scopeRefs.length}</b>{' '}
                    named items rather than the whole checklist.
                  </>
                )}
              </span>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={LABEL}>Period</span>
            <input
              value={periodLabel}
              placeholder={suggestedPeriod === '' ? 'e.g. Q3 2026' : suggestedPeriod}
              onChange={(event) => {
                setPeriodLabel(event.target.value);
              }}
              style={FIELD}
            />
            {periodLabel.trim() === '' && suggestedPeriod !== '' && (
              <span style={{ ...NOTE, fontSize: 10.5 }}>
                Defaults to <span style={MONO}>{suggestedPeriod}</span> from the type&rsquo;s
                cadence.
              </span>
            )}
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={LABEL}>Due date — optional</span>
            <input
              type="date"
              value={dueDate}
              onChange={(event) => {
                setDueDate(event.target.value);
              }}
              style={FIELD}
            />
            <span style={{ ...NOTE, fontSize: 10.5 }}>
              An audit past its due date shows as overdue in the audit list and on the dashboard.
            </span>
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={LABEL}>Auditors</span>
            <div
              style={{
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-md)',
                maxHeight: 132,
                overflowY: 'auto',
              }}
            >
              {(users.data ?? []).map((user) => {
                const on = auditorIds.includes(user.id);
                return (
                  <label
                    key={user.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 9px',
                      borderBottom: '1px solid var(--bd-soft)',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => {
                        setAuditorIds((prev) =>
                          on ? prev.filter((x) => x !== user.id) : [...prev, user.id],
                        );
                      }}
                    />
                    <span style={{ flex: 1 }}>{user.displayName}</span>
                    <span style={{ ...MONO, fontSize: 10, color: 'var(--mu)' }}>{user.orgRole}</span>
                  </label>
                );
              })}
            </div>
            <span style={{ ...NOTE, fontSize: 10.5 }}>
              Assigning nobody is allowed — the audit shows as unassigned rather than silently
              belonging to you.
            </span>
          </div>

          {create.isError && (
            <span style={{ ...NOTE, color: 'var(--ba)' }}>
              {create.error instanceof ApiClientError
                ? create.error.message
                : 'The audit could not be created.'}
            </span>
          )}

          <button
            type="button"
            disabled={auditTypeId === '' || effectivePeriod === '' || create.isPending}
            onClick={() => {
              create.mutate();
            }}
            style={{
              height: 34,
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--ac)',
              background: 'var(--ac)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              opacity: auditTypeId === '' || effectivePeriod === '' ? 0.45 : 1,
            }}
          >
            {create.isPending
              ? 'Creating…'
              : counts === null
                ? 'Create audit'
                : `Create audit · ${String(counts.inScope)} items`}
          </button>
        </div>

        {/* ── the preview ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {auditTypeId === '' || effectivePeriod === '' ? (
            <div
              style={{
                ...CARD,
                padding: '34px 22px',
                alignItems: 'center',
                gap: 8,
                textAlign: 'center',
              }}
            >
              <strong style={{ fontSize: 13 }}>Choose a type to see what would be inherited</strong>
              <span style={{ ...NOTE, maxWidth: 340 }}>
                The preview runs against the project&rsquo;s last closed audit. Nothing is written
                until you press create.
              </span>
            </div>
          ) : preview.isPending ? (
            <div style={{ ...CARD, padding: '20px 16px' }}>
              <span style={NOTE}>Planning the audit…</span>
            </div>
          ) : preview.isError ? (
            <div style={{ ...CARD, padding: '16px', borderColor: 'var(--ba-bd)' }}>
              <span style={{ ...NOTE, color: 'var(--ba)' }}>
                {preview.error instanceof ApiClientError
                  ? preview.error.message
                  : 'The preview could not be computed.'}
              </span>
            </div>
          ) : (
            <>
              <div
                style={{
                  ...CARD,
                  padding: '13px 15px',
                  flexDirection: 'row',
                  gap: 22,
                  alignItems: 'center',
                }}
              >
                <Metric value={preview.data.counts.inScope} caption="in scope" />
                <div style={{ width: 1, height: 30, background: 'var(--bd)' }} />
                <Metric value={preview.data.counts.mandatory} caption="mandatory" />
                <Metric value={preview.data.counts.recommended} caption="recommended" />
                <div style={{ width: 1, height: 30, background: 'var(--bd)' }} />
                <Metric
                  value={preview.data.counts.inherited}
                  caption="inherited"
                  tone="var(--ac)"
                />
                <Metric value={preview.data.counts.toVerify} caption="to verify" />
                <Metric
                  value={preview.data.counts.mustRecheck}
                  caption="must re-check"
                  tone={preview.data.counts.mustRecheck > 0 ? 'var(--wa)' : undefined}
                />
                <span style={{ ...NOTE, marginLeft: 'auto', maxWidth: 210, textAlign: 'right' }}>
                  {preview.data.counts.inherited === 0
                    ? 'Nothing to inherit — this is the project’s first audit of this type.'
                    : `${String(preview.data.counts.inherited)} answers arrive pre-filled and provenance-marked.`}
                </span>
              </div>

              {/* must re-check — the part that must not be a surprise */}
              {preview.data.mustRecheck.length > 0 && (
                <div style={{ ...CARD, borderColor: 'var(--wa-bd)' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '9px 13px',
                      background: 'var(--wab)',
                      borderBottom: '1px solid var(--wa-bd)',
                    }}
                  >
                    <strong style={{ fontSize: 12.5, color: 'var(--wa)' }}>
                      {preview.data.mustRecheck.length} items cannot be carried forward
                    </strong>
                    <span style={{ ...NOTE, color: 'var(--wa)', marginLeft: 'auto' }}>
                      They start Pending, whatever they were last time
                    </span>
                  </div>
                  {preview.data.mustRecheck.slice(0, 8).map((row) => (
                    <div
                      key={row.ref}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '7px 13px',
                        borderBottom: '1px solid var(--bd-soft)',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ ...MONO, fontWeight: 600, width: 42 }}>{row.ref}</span>
                      <StateChip state={row.previousState} size="sm" />
                      <span style={{ ...NOTE, flex: 1 }}>
                        <b style={{ color: 'var(--tx)' }}>
                          {RECHECK_REASON[row.reason].label}
                        </b>{' '}
                        — {RECHECK_REASON[row.reason].detail}
                      </span>
                    </div>
                  ))}
                  {preview.data.mustRecheck.length > 8 && (
                    <div style={{ padding: '7px 13px', background: 'var(--bg)' }}>
                      <span style={NOTE}>
                        and {preview.data.mustRecheck.length - 8} more ·{' '}
                        <span style={MONO}>
                          {preview.data.mustRecheck
                            .slice(8)
                            .map((r) => r.ref)
                            .join(', ')}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* what is inherited */}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...CARD, flex: 1, minWidth: 0, padding: '11px 13px', gap: 7 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ ...LABEL, color: 'var(--ac)' }}>Inherited</span>
                    <span style={{ ...MONO, fontSize: 11, color: 'var(--mu)' }}>
                      {preview.data.inherited.length}
                    </span>
                  </div>
                  <span style={{ ...MONO, fontSize: 10.5, color: 'var(--mu)', lineHeight: 1.6 }}>
                    {preview.data.inherited.length === 0
                      ? '—'
                      : preview.data.inherited.slice(0, 40).join(' · ')}
                    {preview.data.inherited.length > 40 &&
                      ` … +${String(preview.data.inherited.length - 40)}`}
                  </span>
                  <span style={{ ...NOTE, borderTop: '1px solid var(--bd-soft)', paddingTop: 7 }}>
                    Copied with their evidence and marked inherited, so the report can always show
                    which cycle actually did the work.
                  </span>
                </div>

                <div style={{ ...CARD, flex: 1, minWidth: 0, padding: '11px 13px', gap: 7 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={LABEL}>To verify</span>
                    <span style={{ ...MONO, fontSize: 11, color: 'var(--mu)' }}>
                      {preview.data.toVerify.length}
                    </span>
                  </div>
                  <span style={{ ...MONO, fontSize: 10.5, color: 'var(--mu)', lineHeight: 1.6 }}>
                    {preview.data.toVerify.length === 0
                      ? '—'
                      : preview.data.toVerify.slice(0, 40).join(' · ')}
                    {preview.data.toVerify.length > 40 &&
                      ` … +${String(preview.data.toVerify.length - 40)}`}
                  </span>
                  <span style={{ ...NOTE, borderTop: '1px solid var(--bd-soft)', paddingTop: 7 }}>
                    Real work for this cycle. <MarkerChip obligation="MANDATORY" isContextual={false} />{' '}
                    items here are what the score is computed over.
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
